import * as Cesium from 'cesium';
import { computeMetersPerPixelAt } from './printScale';
import { boundsOfPoints, sampleRect, viewportQueryKey, zoomForResolution, } from './googleViewport';
import { scopeGoogleLayer, viewportAttributionResource } from './googleTileCredits';
const controllers = new WeakMap();
const createViewportAttribution = (viewer) => {
    const scoped = new Map();
    const creditsByText = new Map();
    let focus = null;
    const currentQuery = (layer) => {
        var _a;
        const canvas = viewer.scene.canvas;
        const rect = focus !== null && focus !== void 0 ? focus : { x: 0, y: 0, width: canvas.clientWidth, height: canvas.clientHeight };
        const hits = [];
        for (const p of sampleRect(rect)) {
            const hit = viewer.camera.pickEllipsoid(new Cesium.Cartesian2(p.x, p.y), Cesium.Ellipsoid.WGS84);
            if (!hit)
                continue;
            const carto = Cesium.Cartographic.fromCartesian(hit, Cesium.Ellipsoid.WGS84);
            hits.push({ lonDeg: Cesium.Math.toDegrees(carto.longitude), latDeg: Cesium.Math.toDegrees(carto.latitude) });
        }
        const bounds = boundsOfPoints(hits);
        if (!bounds)
            return null;
        // Centre first; a tilted view can put the centre past the horizon, so
        // fall back to the bottom edge, which is nearest the camera.
        const cx = rect.x + rect.width / 2;
        const mpp = (_a = computeMetersPerPixelAt(viewer, cx, rect.y + rect.height / 2)) !== null && _a !== void 0 ? _a : computeMetersPerPixelAt(viewer, cx, rect.y + rect.height - 1);
        if (mpp === null)
            return null;
        // Cesium picks tile levels against drawing-buffer pixels, not CSS pixels.
        const bufferPerCssPx = canvas.clientWidth > 0 ? canvas.width / canvas.clientWidth : 1;
        const zoom = zoomForResolution(mpp / bufferPerCssPx, (bounds.north + bounds.south) / 2, layer.tileWidth, layer.minLevel, layer.maxLevel);
        return { ...bounds, zoom };
    };
    const show = (layer, copyright) => {
        var _a;
        let credit = null;
        if (copyright) {
            credit = (_a = creditsByText.get(copyright)) !== null && _a !== void 0 ? _a : new Cesium.Credit(copyright, false);
            creditsByText.set(copyright, credit);
        }
        layer.credits.splice(layer.baseCount, layer.credits.length - layer.baseCount, ...(credit ? [credit] : []));
        if (!viewer.isDestroyed())
            viewer.scene.requestRender();
    };
    const refreshLayer = (layer) => {
        const q = currentQuery(layer);
        const key = q ? viewportQueryKey(q) : null;
        if (key !== null && key === layer.lastKey)
            return;
        layer.lastKey = key;
        const seq = ++layer.requestSeq;
        if (!q) {
            show(layer, null);
            return;
        }
        const failed = (err) => {
            if (seq !== layer.requestSeq)
                return;
            layer.lastKey = null;
            console.error('Google viewport attribution request failed', err);
        };
        const pending = viewportAttributionResource(layer.google, q).fetchJson();
        if (!pending) {
            failed(new Error('request was not scheduled'));
            return;
        }
        pending
            .then((json) => {
            if (seq !== layer.requestSeq)
                return;
            show(layer, typeof (json === null || json === void 0 ? void 0 : json.copyright) === 'string' ? json.copyright.trim() : null);
        })
            .catch(failed);
    };
    const refresh = () => scoped.forEach(refreshLayer);
    const watch = (imageryLayer) => {
        const provider = imageryLayer.imageryProvider;
        if (!provider || scoped.has(provider))
            return;
        const layer = scopeGoogleLayer(provider);
        if (!layer)
            return;
        scoped.set(provider, layer);
        refreshLayer(layer);
    };
    for (let i = 0; i < viewer.imageryLayers.length; i++)
        watch(viewer.imageryLayers.get(i));
    viewer.imageryLayers.layerAdded.addEventListener(watch);
    viewer.imageryLayers.layerRemoved.addEventListener((imageryLayer) => {
        scoped.delete(imageryLayer.imageryProvider);
    });
    // `changed` fires partway through a long move (and through the welcome
    // globe's spin, which never ends); moveEnd catches where the camera settles.
    viewer.camera.changed.addEventListener(refresh);
    viewer.camera.moveEnd.addEventListener(refresh);
    return {
        setFocus: (rect) => {
            focus = rect;
            refresh();
        },
    };
};
export const attachGoogleViewportAttribution = (viewer) => {
    if (!controllers.has(viewer))
        controllers.set(viewer, createViewportAttribution(viewer));
};
// Narrow the attribution to part of the canvas (the print map window), or
// back to the whole canvas with null.
export const setGoogleAttributionFocus = (viewer, rect) => {
    var _a;
    (_a = controllers.get(viewer)) === null || _a === void 0 ? void 0 : _a.setFocus(rect);
};
